import React from 'react';
import { CheckCircle, Clock, XCircle, FileText, Edit, Eye, UserX } from 'lucide-react';
import { LinkStatus, Evidence } from '../../../types/pilgrimGuide';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  status: LinkStatus;
  evidence?: Evidence;
  hasEvidence?: boolean;
  isOwner?: boolean;
  onSubmitEvidence?: () => void;
  onViewEvidence?: () => void;
  onEditEvidence?: () => void;
  onToggleOwnership?: () => void;
};

export const StatusBadge = ({
  status,
  evidence,
  hasEvidence = false,
  isOwner,
  onSubmitEvidence,
  onViewEvidence,
  onEditEvidence,
  onToggleOwnership
}: Props) => {
  const { translations } = useLanguage();
  const hasEvidenceSubmission = hasEvidence || Boolean(evidence);

  const config = {
    approved: {
      icon: CheckCircle,
      text: translations?.statusBadge?.statuses?.approved || 'Approved',
      colors: 'bg-green-50 text-green-600',
    },
    disputed: {
      icon: XCircle,
      text: translations?.statusBadge?.statuses?.disputed || 'Disputed',
      colors: 'bg-red-50 text-red-600',
    },
    rejected: {
      icon: XCircle,
      text: translations?.statusBadge?.statuses?.rejected || 'Rejected',
      colors: 'bg-gray-50 text-gray-600',
    },
    pending: {
      icon: Clock,
      text: translations?.statusBadge?.statuses?.pending || 'Pending',
      colors: 'bg-yellow-50 text-yellow-600',
    },
    waiting_identification: {
      icon: Clock,
      text: translations?.statusBadge?.statuses?.waitingIdentification || 'Waiting Identification',
      colors: 'bg-blue-50 text-blue-600',
    },
  };

  const StatusIcon = config[status].icon;

  const handleButtonClick = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Status Badge */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm ${config[status].colors} whitespace-nowrap`}
        >
          <StatusIcon size={16} />
          {config[status].text}
        </span>
        
        {/* Not Owner Badge */}
        {status === 'approved' && isOwner === false && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm bg-orange-50 text-orange-600 whitespace-nowrap">
            <UserX size={16} />
            {translations?.statusBadge?.notOwner || 'Not Owner'}
          </span>
        )}
      </div>

      {/* Evidence Actions */}
      {status === 'disputed' && (
        <div className="flex flex-wrap gap-2">
          {hasEvidenceSubmission ? (
            <>
              {/* View Evidence Button */}
              {onViewEvidence && (
                <button
                  onClick={(e) => handleButtonClick(e, onViewEvidence)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Eye size={16} />
                  <span>
                    {translations?.statusBadge?.actions?.viewEvidence ||
                      'View Evidence'}
                  </span>
                </button>
              )}
              
              {/* Edit Evidence Button */}
              {onEditEvidence && (
                <button
                  onClick={(e) => handleButtonClick(e, onEditEvidence)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Edit size={16} />
                  <span>
                    {translations?.statusBadge?.actions?.editEvidence || 'Edit'}
                  </span>
                </button>
              )}

              {!onViewEvidence && !onEditEvidence && (
                <div className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg">
                  <FileText size={16} />
                  <span>
                    {translations?.evidenceModal?.submitSuccess ||
                      'Evidence submitted successfully'}
                  </span>
                </div>
              )}
            </>
          ) : (
            /* Submit Evidence Button */
            onSubmitEvidence && (
              <button
                onClick={(e) => handleButtonClick(e, onSubmitEvidence)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <FileText size={16} />
                <span>
                  {translations?.statusBadge?.actions?.submitEvidence ||
                    'Submit Evidence'}
                </span>
              </button>
            )
          )}
        </div>
      )}

      {/* Toggle Ownership Button - Only for admin */}
      {status === 'approved' && onToggleOwnership && (
        <button
          onClick={(e) => handleButtonClick(e, onToggleOwnership)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors"
        >
          <UserX size={16} />
          <span>
            {isOwner === false
              ? translations?.statusBadge?.actions?.makeOwner || 'Make Owner'
              : translations?.statusBadge?.actions?.makeNotOwner || 'Make Not Owner'}
          </span>
        </button>
      )}
    </div>
  );
};
